/**
 * An open M-accounting project: Mike's tabs, with Accounting's analysis tools beside them.
 *
 * What-If and Sensitivity are the SAME views Accounting uses. They read finished results
 * through AnalysisSourceProvider, and the server computes those results from Mike's
 * workbook rather than from the IAS statements - so the two disciplines share the views
 * and nothing else.
 */

import { useMemo, useState } from 'react';
import { AnalysisSourceProvider } from '../../../components/financial/AnalysisSourceContext';
import { useMAccountingStore } from '../../../store/m-accounting';
import type { ModelSettings } from '../../../types/financial-model';
import { SensitivityView } from '../../SensitivityView';
import { WhatIfView } from '../../WhatIfView';
import DisciplineSwitch from '../DisciplineSwitch';
import type { Discipline, DisciplineId } from '../disciplines';
import { MCharts } from './MCharts';
import { MGrid } from './MGrid';
import { MPeriods } from './MPeriods';
import { MOverview } from './MOverview';
import { MAutofix } from './MAutofix';
import { MVersions } from './MVersions';
import { MForecast } from './MForecast';
import { MBacktest } from './MBacktest';
import { WorkspaceShell } from '../ui/WorkspaceShell';
import { SaveState } from '../ui/SaveState';
import { Button } from '../ui/Button';
import { FormulasPanelV2 } from '../FormulasPanelV2';
import { baselineFormulaGroups } from './mFormulas';

interface TabGroup {
  label: string;
  tabs: { id: string; label: string; sheet?: string }[];
}

/** Accounting's tabs, less what Mike's model has no rows for (Changes in Equity is IAS). */
const GROUPS: TabGroup[] = [
  {
    label: 'Accounts',
    tabs: [
      { id: 'm-overview', label: 'Overview' },
      { id: 'm-pnl', label: 'P&L', sheet: 'P&L ' },
      { id: 'm-cashflow', label: 'Cashflow', sheet: '(AI) Cashflow' },
      { id: 'm-balance-sheet', label: 'Balance Sheet', sheet: '(AI) Balance Sheet' },
      { id: 'm-cogs', label: 'COGS', sheet: 'COGS' },
      { id: 'm-indirect', label: 'Indirect Costs', sheet: 'Indirect costs' },
      { id: 'm-fixed-assets', label: 'Fixed Assets & Depn', sheet: 'Fixed Assets & Depn' },
      { id: 'm-staffing', label: 'Staffing', sheet: 'Staffing' },
      { id: 'm-control', label: 'Control Account', sheet: '(AI) Control Account' },
      { id: 'm-charts', label: 'Charts' },
    ],
  },
  {
    label: 'Analysis',
    tabs: [
      { id: 'm-what-if', label: 'What-If' },
      { id: 'm-sensitivity', label: 'Sensitivity' },
      { id: 'm-forecast', label: 'AI Forecast' },
      { id: 'm-backtest', label: 'Backtest' },
    ],
  },
  {
    label: '',
    tabs: [
      { id: 'm-versions', label: 'Versions' },
      { id: 'm-settings', label: 'Settings', sheet: 'Settings' },
    ],
  },
];

const ALL_TABS = GROUPS.flatMap((g) => g.tabs);

export function MWorkspace({
  discipline,
  onSwitchDiscipline,
}: {
  discipline: Discipline;
  onSwitchDiscipline: (id: DisciplineId) => void;
}) {
  const project = useMAccountingStore((s) => s.project);
  const computed = useMAccountingStore((s) => s.computed);
  const activeTab = useMAccountingStore((s) => s.activeTab);
  const setActiveTab = useMAccountingStore((s) => s.setActiveTab);
  const isSaving = useMAccountingStore((s) => s.isSaving);
  const isDirty = useMAccountingStore((s) => s.isDirty);
  const error = useMAccountingStore((s) => s.error);
  const dismissError = useMAccountingStore((s) => s.dismissError);
  const close = useMAccountingStore((s) => s.close);
  const saveNow = useMAccountingStore((s) => s.saveNow);
  const updateConfig = useMAccountingStore((s) => s.updateConfig);
  const fixes = useMAccountingStore((s) => s.fixes);
  const loadFixes = useMAccountingStore((s) => s.loadFixes);
  const exportProject = useMAccountingStore((s) => s.exportProject);
  const [showFormulas, setShowFormulas] = useState(false);
  const formulaGroups = useMemo(
    () => (computed ? baselineFormulaGroups(computed, fixes) : []),
    [computed, fixes],
  );

  if (!project || !computed) return null;

  const tab = ALL_TABS.find((t) => t.id === activeTab) ?? ALL_TABS[0];
  const sheet = tab.sheet ? computed.tabs.find((t) => t.sheet === tab.sheet) : undefined;

  const body = (() => {
    switch (tab.id) {
      case 'm-overview':
        return (
          <div className="space-y-6">
            <MAutofix />
            <MOverview computed={computed} source={project.source} />
          </div>
        );
      case 'm-charts':
        return <MCharts computed={computed} />;
      case 'm-what-if':
        return <WhatIfView />;
      case 'm-sensitivity':
        return <SensitivityView />;
      case 'm-forecast':
        return <MForecast />;
      case 'm-backtest':
        return <MBacktest />;
      case 'm-versions':
        return <MVersions />;
      default:
        if (!sheet) return null;
        return (
          <>
            {sheet.sheet === 'Settings' && <MPeriods computed={computed} />}
            <MGrid tab={sheet} historical={computed.historical} />
          </>
        );
    }
  })();

  return (
    <WorkspaceShell
      discipline={discipline}
      onBack={close}
      title={project.name}
      subtitle={<>Baseline model · version {project.version}</>}
      status={<SaveState isSaving={isSaving} isDirty={isDirty} />}
      switcher={<DisciplineSwitch current={discipline} onSwitch={onSwitchDiscipline} />}
      groups={GROUPS.map((group, index) => ({ id: group.label || `group-${index}`, label: group.label, tabs: group.tabs }))}
      active={tab.id}
      onSelect={setActiveTab}
      tools={
        <>
          <Button
            size="sm"
            onClick={() => {
              void loadFixes();
              setShowFormulas(true);
            }}
          >
            Formulas
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void exportProject()}>
            Export Excel
          </Button>
        </>
      }
      banner={
        error && (
          <div className="mx-4 mt-3 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <span>{error}</span>
            <button type="button" onClick={dismissError} className="shrink-0 text-xs underline">Dismiss</button>
          </div>
        )
      }
    >
      <div className="p-4 sm:p-6">
        <AnalysisSourceProvider
          value={{
            settings: computed.settings as unknown as ModelSettings,
            annualSummary: computed.annualSummary,
            whatIf: computed.whatIf,
            sensitivity: computed.sensitivity,
            valuation: null,
            isRecalculating: isSaving,
            isDirty,
            updateSetting: (key, value) => {
              if (key === 'whatIfConfig' || key === 'sensitivityConfig') void updateConfig(key, value);
            },
            recalculate: saveNow,
          }}
        >
          {body}
        </AnalysisSourceProvider>
      </div>
      <FormulasPanelV2
        open={showFormulas}
        onClose={() => setShowFormulas(false)}
        groups={formulaGroups}
        subtitle="The workbook's own formulas, cell by cell, and the fixes that replaced them"
        footer="Read from the cells the model evaluates - the export carries the same formulas, live."
        placeholder="Search every tab - e.g. EBITDA, VAT, B2"
      />
    </WorkspaceShell>
  );
}
